import { t } from '@trimble/i18n';
import { Component, createMemo, onMount, Show } from 'solid-js';
import { ColorPicker } from '../color-picker/ColorPicker';
import { BasicButton } from '../toolbar/toolbar-controls/BasicButton';
import { TrbToolbar } from '../toolbar/TrbToolbar';
import { ControlDropdown } from '../control-dropdown/ControlDropdown';
import type { ControlMenuItemType } from '../control-menu';
import tinycolor from 'tinycolor2';

export interface ObjectSelectionToolbarProps {
  selectionCount: number;
  initialColor?: { r: number; g: number; b: number };
  initialOpacity?: number;
  colorPickerOpen: boolean;
  customClasses?: string;
  /** If true, the toolbar will be rendered in dark mode regardless of the system theme. Default is false.*/
  darkMode?: boolean;
  onFit?(): void;
  onClip?(): void;
  onHideSelection?(): void;
  onShowOnlySelected?(): void;
  onShowAll?(): void;
  isHideSelectionActive?: boolean;
  isShowOnlySelectedActive?: boolean;
  onColorChange?(color: { r: number; g: number; b: number }): void;
  onColorSave?(color: { r: number; g: number; b: number }): void;
  onColorPickerToggle(open: boolean): void;
  onOpacityChange?(opacity: number): void;
  useTransparency?: boolean;
  showResetButton?: boolean;
  suppressColorSelection?: boolean;
  onIsolateLayers?(): void;
  onUnisolateLayers?(): void;
  onReset?(): void;
  onPropertiesToggle?(): void;
  isPropertiesPanelActive?: boolean;
  onCreateSurface?(): void;
  createSurfaceTooltip?: string;
  isCreateSurfacePending?: boolean;
  onToggleVisibility?(): void;
  isHidden?: boolean;
  onDelete?(): void;
}

export const ObjectSelectionToolbar: Component<ObjectSelectionToolbarProps> = (
  props
) => {
  const selectionLabel = createMemo((): string => {
    return t('objectSelectionToolbar.selected', {
      count: props.selectionCount,
    });
  });

  const initialColorHex = createMemo(() => {
    return props.initialColor
      ? tinycolor(props.initialColor).toHexString()
      : undefined;
  });

  const changeVisibilityItems = createMemo((): ControlMenuItemType[] => {
    const items: ControlMenuItemType[] = [];

    const onShowAll = props.onShowAll;
    if (onShowAll) {
      items.push({
        id: 'showAll',
        type: 'action',
        label: t('objectSelectionToolbar.showAll'),
        onClick: () => onShowAll(),
      });
    }

    const onIsolateLayers = props.onIsolateLayers;
    const onUnisolateLayers = props.onUnisolateLayers;

    if (onShowAll && (onIsolateLayers || onUnisolateLayers)) {
      items.push({
        id: 'divider',
        type: 'divider',
      });
    }

    if (onIsolateLayers) {
      items.push({
        id: 'isolateLayers',
        type: 'action',
        label: t('objectSelectionToolbar.isolateLayers'),
        onClick: () => onIsolateLayers(),
      });
    }

    if (onUnisolateLayers) {
      items.push({
        id: 'unisolateLayers',
        type: 'action',
        label: t('objectSelectionToolbar.unisolateLayers'),
        onClick: () => onUnisolateLayers(),
      });
    }

    return items;
  });

  // Override to enable dark mode for the toolbar. Colors are copied from tcweb.
  const darkModeThemeOverride = (): void => {
    const styleId = 'object-select-dark-theme';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .force-dark-theme {
          --modus-wc-color-base-page: #363545;
          --modus-wc-color-base-content: #ffffff;

          & .dropdown-container button,
          & .button.tertiary
          {
            color: #ffffff;
          }

          & .dropdown-container button:hover,
          & .dropdown-container button:active,
          & .button.tertiary:hover,
          & .button.tertiary:active
          {
            background-color: #6A6976;
          }

          & .button.active
          {
            background-color: #8c8b96 !important;

            & > i
            {
              color: #ffffff !important;
            }
          }
        }
      `;
      document.head.appendChild(style);
    }
  };

  onMount(darkModeThemeOverride);

  return (
    <div class={(props.darkMode ?? false) ? 'force-dark-theme' : ''}>
      <TrbToolbar
        orientation="horizontal"
        addSeparators={false}
        customClasses={props.customClasses}
      >
        <div class="contents" data-testid="object-selection-toolbar">
          <span
            class="text-sm text-base-content whitespace-nowrap px-1"
            data-testid="object-selection-toolbar-count"
          >
            {selectionLabel()}
          </span>
          <Show when={props.onCreateSurface} keyed>
            {(onCreateSurface) => (
              <Show
                when={!props.isCreateSurfacePending}
                fallback={
                  <div
                    class="w-10 h-10 flex items-center justify-center"
                    data-testid="object-selection-toolbar-create-surface-busy"
                    role="status"
                    title={t('general.loading')}
                  >
                    <modus-spinner class="m-auto" color="primary" size="20px" />
                  </div>
                }
              >
                <span data-testid="object-selection-toolbar-create-surface">
                  <BasicButton
                    iconName="svg-surface"
                    tooltip={
                      props.createSurfaceTooltip ??
                      t('objectSelectionToolbar.createSurfaceTooltip')
                    }
                    action={onCreateSurface}
                    size="medium"
                  />
                </span>
              </Show>
            )}
          </Show>
          <Show when={props.onPropertiesToggle} keyed>
            {(onPropertiesToggle) => (
              <span data-testid="object-selection-toolbar-properties">
                <BasicButton
                  iconName="tc-icon-info"
                  tooltip={t('objectSelectionToolbar.propertiesPanelTooltip')}
                  action={onPropertiesToggle}
                  size="medium"
                  pressed={props.isPropertiesPanelActive ?? false}
                />
              </span>
            )}
          </Show>
          <Show when={props.onFit} keyed>
            {(fit) => (
              <span data-testid="object-selection-toolbar-fit">
                <BasicButton
                  iconName="tc-icon-view-fit"
                  tooltip={t('objectSelectionToolbar.fitToSelectionTooltip')}
                  action={fit}
                  size="medium"
                />
              </span>
            )}
          </Show>
          <Show when={props.onClip} keyed>
            {(clip) => (
              <span data-testid="object-selection-toolbar-crop">
                <BasicButton
                  iconName="svg-clip-box"
                  tooltip={t('objectSelectionToolbar.clipBoxTooltip')}
                  action={clip}
                  size="medium"
                />
              </span>
            )}
          </Show>
          <Show when={props.onColorChange || props.onColorSave}>
            <div
              data-testid="object-selection-toolbar-color-picker"
              class="relative"
            >
              <BasicButton
                iconName="paint_bucket"
                tooltip={t('objectSelectionToolbar.colorPickerTooltip')}
                action={() => props.onColorPickerToggle(!props.colorPickerOpen)}
                size="medium"
                pressed={props.colorPickerOpen}
              />
              <Show when={props.colorPickerOpen}>
                <ColorPicker
                  useTransparency={props.useTransparency ?? true}
                  showResetButton={props.showResetButton ?? true}
                  suppressSelection={props.suppressColorSelection}
                  initialColor={initialColorHex()}
                  initialTransparency={1 - (props.initialOpacity ?? 1)}
                  onChange={(rgb) => {
                    props.onColorChange?.(rgb);
                  }}
                  onSave={(rgb) => {
                    props.onColorSave?.(rgb);
                  }}
                  onTransparencyChange={(transparency) => {
                    props.onOpacityChange?.(1 - transparency);
                  }}
                  onReset={() => {
                    props.onReset?.();
                  }}
                />
              </Show>
            </div>
          </Show>
          <Show when={props.onHideSelection} keyed>
            {(hideSelection) => (
              <span data-testid="object-selection-toolbar-hide-selection">
                <BasicButton
                  iconName="tc-icon-eye-visibility-off"
                  tooltip={t('objectSelectionToolbar.hideSelectionTooltip')}
                  action={hideSelection}
                  size="medium"
                  pressed={props.isHideSelectionActive}
                />
              </span>
            )}
          </Show>
          <Show when={props.onShowOnlySelected} keyed>
            {(showOnlySelected) => (
              <span data-testid="object-selection-toolbar-show-only-selected">
                <BasicButton
                  iconName="tc-icon-hide-others"
                  tooltip={t('objectSelectionToolbar.showOnlySelectedTooltip')}
                  action={showOnlySelected}
                  size="medium"
                  pressed={props.isShowOnlySelectedActive}
                />
              </span>
            )}
          </Show>
          <Show when={props.onToggleVisibility} keyed>
            {(toggleVisibility) => (
              <span data-testid="object-selection-toolbar-toggle-visibility">
                <BasicButton
                  iconName={
                    props.isHidden
                      ? 'tc-icon-eye-visibility-off'
                      : 'tc-icon-eye-visibility'
                  }
                  tooltip={
                    props.isHidden
                      ? t('objectSelectionToolbar.showSelectionTooltip')
                      : t('objectSelectionToolbar.hideSelectionTooltip')
                  }
                  action={toggleVisibility}
                  size="medium"
                />
              </span>
            )}
          </Show>
          <Show when={changeVisibilityItems().length > 0 || props.onDelete}>
            <span class="mx-[4px] w-[1px] h-10 bg-white" />
            <Show when={changeVisibilityItems().length > 0}>
              <span
                data-testid="object-selection-toolbar-change-visibility"
                class="dropdown-container"
              >
                <ControlDropdown
                  anchorIcon={
                    <div class="whitespace-nowrap leading-10">
                      {t('objectSelectionToolbar.changeVisibility')}
                    </div>
                  }
                  items={changeVisibilityItems()}
                  position="right"
                />
              </span>
            </Show>
            <Show when={props.onDelete} keyed>
              {(onDelete) => (
                <span data-testid="object-selection-toolbar-delete">
                  <BasicButton
                    iconName="delete"
                    tooltip={t('objectSelectionToolbar.deleteTooltip')}
                    action={onDelete}
                    size="medium"
                  />
                </span>
              )}
            </Show>
          </Show>
        </div>
      </TrbToolbar>
    </div>
  );
};
